import './add-permissions-page.scss';
import PageHeader from 'modules/page-header';
import { Loader } from '@ru-digital-assets/ui-component';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'hooks/redux';
import OperatorAPI from 'services/api/Operator';
import { setPush } from 'store/actions/app';
import {
  ISignerInfo,
  PermissionToLabel,
  SignerPermissons,
} from 'constants/signers';
import PageCard from 'containers/PageCard';
import { useHistory } from 'react-router';
import { LightFooter, KeySelectionFooter, RadioButton } from 'components';
import {
  IconBtn,
  UiSelect,
  UiDatePicker,
} from '@ru-digital-assets/ui-component';
import { ArrowRight } from 'assets/icons';
import { OptionType } from '@ru-digital-assets/ui-component/dist/stories/ui/ui-select/ui-select';
import { getFormatedDate } from 'utils';
import { showLoader, hideLoader } from 'store/actions/loader';

enum ProcessStep {
  SELECT_PERMISSIONS = 'SELECT_PERMISSIONS',
  SIGN_PERMISSIONS = 'SIGN_PERMISSIONS',
}

export default function AddPermissionsPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const token = useSelector((s) => s.auth.account.token);
  const [signersList, setSignersList] = useState<ISignerInfo[]>();
  const [selectedSigner, setSelectedSigner] = useState<ISignerInfo>();
  const [
    selectedPermission,
    setSelectedPermission,
  ] = useState<SignerPermissons>();
  const [dates, setDates] = useState({} as Record<SignerPermissons, string>);

  const [processStep, setProcessStep] = useState(
    ProcessStep.SELECT_PERMISSIONS
  );

  useEffect(() => {
    OperatorAPI.getSigners(token)
      .then((response) => {
        setSignersList(response.data);
      })
      .catch(() => {
        dispatch(
          setPush({
            type: 'error',
            message: 'Не удалось загрузить информацию об исполнителях',
          })
        );
      });
  }, [token]);

  const signersSelectOptions = useMemo(() => {
    if (!signersList) return [];
    return signersList.map((signer) => ({
      label: signer.displayName,
      value: signer,
    }));
  }, [signersList]);

  const onClose = useCallback(() => {
    history.push('/profile/company', { tab: 'signers' });
  }, []);

  const onGoToSignClick = useCallback(() => {
    setProcessStep(ProcessStep.SIGN_PERMISSIONS);
  }, []);

  const isGoToSignButtonAvailable = useMemo(() => {
    if (!selectedSigner || !selectedPermission || !dates[selectedPermission])
      return false;
    return true;
  }, [selectedSigner, selectedPermission, dates]);

  const onSignPermissions = useCallback(
    (privateKey: string) => {
      dispatch(showLoader());
      OperatorAPI.grantPermissionToSigner(token, {
        userId: selectedSigner!.userId,
        permissionType: selectedPermission!,
        expiryDate: dates[selectedPermission!],
        privateKey,
      })
        .then(() => {
          dispatch(
            setPush({
              type: 'success',
              message: 'Полномочие успешно создано',
            })
          );
          history.push(`/operator/signer-card/${selectedSigner!.userId}`, {
            id: selectedSigner!.userId,
          });
        })
        .catch(() => {
          dispatch(
            setPush({
              type: 'error',
              message: 'Ошибка при инициализации выдачи полномочий',
            })
          );
        })
        .finally(() => {
          dispatch(hideLoader());
        });
    },
    [token, selectedSigner, selectedPermission]
  );

  return (
    <div className="add-permissions-page">
      <PageHeader
        items={[
          { link: '/profile/company', title: 'Профиль компании' },
          { link: '#', title: 'Карточка исполнителя' },
        ]}
        iconType="company"
        isHideBalanceInformer
      />
      {signersList ? (
        <PageCard
          title="Добавление полномочия"
          onClose={onClose}
          minHeight="calc(100vh - 102px)"
          main={
            <div className="add-permissions-page__main">
              <p className="p p_bold mb_16">Личные данные:</p>
              {processStep === ProcessStep.SELECT_PERMISSIONS ? (
                <div style={{ width: '370px' }} className="mb_32">
                  <UiSelect
                    placeholder="Выберите исполнителя"
                    options={signersSelectOptions}
                    onChange={(selected) => {
                      if (selected) {
                        setSelectedSigner((selected as OptionType).value);
                      }
                    }}
                  />
                </div>
              ) : (
                <p className="p p_font-size_18 mb_32">
                  {selectedSigner!.displayName}
                </p>
              )}
              {selectedSigner && (
                <>
                  <p className="p p_bold mb_16">Полномочия:</p>
                  {processStep === ProcessStep.SELECT_PERMISSIONS &&
                    Object.keys(SignerPermissons).map((permissionType) => {
                      const permission = permissionType as SignerPermissons;
                      return (
                        <div className="add-permissions-page__permission">
                          <RadioButton
                            label={PermissionToLabel[permission]}
                            onChange={() => setSelectedPermission(permission)}
                            checked={permission === selectedPermission}
                            disabled={!!selectedSigner.permissions[permission]}
                          />
                          <div className="flex align-center">
                            <span className="p p_font-size_12 p_color_medium-grey mr_16">
                              Срок действия
                              <br />
                              полномочий
                            </span>
                            <UiDatePicker
                              placeholder="Срок действия полномочий"
                              selected={dates[permission]}
                              disabled={
                                !!selectedSigner.permissions[permission]
                              }
                              minDate={new Date()}
                              dateFormat="dd MMMM yyyy"
                              onChange={(value: string) =>
                                setDates({
                                  ...dates,
                                  [permission]: value,
                                })
                              }
                            />
                          </div>
                        </div>
                      );
                    })}
                  {processStep === ProcessStep.SIGN_PERMISSIONS && (
                    <div className="add-permissions-page__permission">
                      <p className="p p_color_medium-grey">
                        {PermissionToLabel[selectedPermission!]}
                      </p>
                      <div>
                        <p className="p p_font-size_12 p_color_medium-grey mr_16">
                          Срок действия полномочий
                        </p>
                        <p className="p">
                          {getFormatedDate(
                            dates[selectedPermission!],
                            'dd MMMM yyyy'
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                  <p className="p">
                    Добавление полномочия необходимо подписать Ключом, иначе
                    данные не сохранятся.
                  </p>
                </>
              )}
            </div>
          }
          footer={
            processStep === ProcessStep.SELECT_PERMISSIONS ? (
              <LightFooter>
                <div className="flex justify-between align-center">
                  <button className="button button_delete" onClick={onClose}>
                    Отмена
                  </button>
                  <IconBtn
                    title="Перейти к подписанию"
                    disabled={!isGoToSignButtonAvailable}
                    icon={<ArrowRight color="#fff" />}
                    onClick={onGoToSignClick}
                  />
                </div>
              </LightFooter>
            ) : (
              <KeySelectionFooter
                onBackButtonClick={() =>
                  setProcessStep(ProcessStep.SELECT_PERMISSIONS)
                }
                backButtonText="Вернуться"
                submitButtonTitle={
                  'Подписать транзакцию\nи добавить исполнителя'
                }
                onSubmit={onSignPermissions}
              />
            )
          }
        />
      ) : (
        <div className="flex justify-center mt_32">
          <Loader />
        </div>
      )}
    </div>
  );
}
